import equal from 'fast-deep-equal';
import { html, css } from 'lit';

import '../../../../packages/neb-lit-components/src/components/inputs/neb-select-search';
import { BUTTON_ROLE } from '../../../../packages/neb-lit-components/src/components/neb-button';
import NebPopup, {
  ELEMENTS as BASE_ELEMENTS,
} from '../../../../packages/neb-popup/src/neb-popup';
import {
  centsToCurrency,
  currencyToCentsWithNegative,
  currencyToCents,
} from '../../../../packages/neb-utils/formatters';
import * as masks from '../../../../packages/neb-utils/masks';
import { isActivePatientPaymentCode } from '../../../../packages/neb-utils/neb-ledger-util';
import { deepCopy } from '../../../../packages/neb-utils/utils';
import {
  CSS_COLOR_ERROR,
  CSS_FONT_FAMILY,
  CSS_SPACING,
  CSS_COLOR_HIGHLIGHT,
  CSS_SPACING_ROW,
  CSS_FONT_SIZE_CAPTION,
  CSS_FIELD_MARGIN,
} from '../../../styles';
import { searchItems } from '../../../utils/filters';

const HEADER = ['Insurance', 'Type', 'Amount', 'Paid'];

const BLANK_TYPE_DROPDOWN_ITEM = {
  label: '',
  data: {
    id: '',
    code: '',
    description: '',
    label: '',
  },
};

export const ELEMENTS = {
  ...BASE_ELEMENTS,
  fieldHeader: { selector: '[id^=field-header-]' },
  insuranceSelect: { selector: '[id^=insurance-select-]' },
  typeSearchSelect: { selector: '[id^=type-search-]' },
  amountTextfields: { selector: '[id^=amount-textfield-]' },
  paidAmountValue: { selector: '[id^=paid-amount-value-]', tag: 'neb-text' },
  buttonSave: { id: 'save' },
  buttonCancel: { id: 'cancel' },
  displayedErrors: { id: 'displayed-errors' },
  fieldContainer: { id: 'field-container' },
};

class NebPopupOtherResponsibility extends NebPopup {
  static get properties() {
    return {
      __typeState: Array,
      __errors: Array,
      __searchResult: Array,
      __showResults: Boolean,
      __searchItems: Array,
    };
  }

  initState() {
    super.initState();
    this.model = {
      title: '',
      initialDebits: [],
      patientInsurances: [],
      paymentAmount: 0,
      paymentAllocated: 0,
    };

    this.paidAmountIsRemoved = false;
    this.initialTypeState = [];

    this.__patientInsuranceDropdownItems = [];
    this.__typeState = [];
    this.__errors = [];
    this.__itemsToDelete = [];
    this.__searchResult = [];
    this.__showResults = false;
    this.__searchItems = [];
  }

  initHandlers() {
    this.handlers = {
      changeAmount: ({ name, value }) => {
        const rowIndex = Number(name.split('-')[2]);
        const isValueNegative = value.charAt(0) === '-';

        if (value) {
          const stateCopy = [...this.__typeState];

          stateCopy[rowIndex] = {
            ...stateCopy[rowIndex],
            amount: isValueNegative
              ? currencyToCentsWithNegative(value)
              : currencyToCents(value),
          };

          this.__typeState = stateCopy;

          const lastRowIndex = this.__typeState.length - 1;

          if (value !== '$0.00' && rowIndex === lastRowIndex) {
            this.__addNewRow();
          }

          if (this.__isRowEmpty(rowIndex)) {
            this.__removeRow(rowIndex);
            return;
          }
        }

        this.__validate(rowIndex);
      },
      changeType: ({ name, value }) => {
        const rowIndex = Number(name.split('-')[2]);

        if (!value) {
          const stateCopy = [...this.__typeState];
          stateCopy[rowIndex] = {
            ...stateCopy[rowIndex],
            codeId: '',
          };

          this.__typeState = stateCopy;
        }

        const { data } = value;

        if (data) {
          const stateCopy = [...this.__typeState];

          stateCopy[rowIndex] = {
            ...stateCopy[rowIndex],
            codeId: data.id,
          };

          this.__typeState = stateCopy;

          const lastRowIndex = this.__typeState.length - 1;

          if (rowIndex === lastRowIndex) {
            this.__addNewRow();
          }
        }

        if (this.__isRowEmpty(rowIndex)) {
          this.__removeRow(rowIndex);
          return;
        }
        this.__validate(rowIndex);
      },
      changeInsurance: ({ name, value }) => {
        const rowIndex = Number(name.split('-')[2]);

        if (!value) {
          const stateCopy = [...this.__typeState];
          stateCopy[rowIndex] = {
            ...stateCopy[rowIndex],
            patientInsuranceId: '',
          };

          this.__typeState = stateCopy;
        }

        const { data } = value;

        if (data.id === this.model.primaryInsuranceId) {
          const index = Number(name.split('-')[2]);

          this.handlers.changeType({
            name: `type-search-${index}`,
            value: BLANK_TYPE_DROPDOWN_ITEM,
          });
        }

        if (data) {
          const stateCopy = [...this.__typeState];

          stateCopy[rowIndex] = {
            ...stateCopy[rowIndex],
            patientInsuranceId: data.id,
            payerId: data.payerPlanId,
          };

          this.__typeState = stateCopy;

          const lastRowIndex = this.__typeState.length - 1;

          if (rowIndex === lastRowIndex) {
            this.__addNewRow();
          }
        }

        if (this.__isRowEmpty(rowIndex)) {
          this.__removeRow(rowIndex);
          return;
        }
        this.__validate(rowIndex);
      },
      save: () => {
        if (!this.__hasErrors()) {
          const result = this.__resolvePopupResult();

          this.onClose(result);
        }
      },
      cancel: () => {
        this.onClose(false);
      },
      keydown: e => {
        if (e.key === 'Escape') {
          this.handlers.cancel();
        }
      },
      searchCode: ({ value }) => {
        if (!value) this.__showResults = true;
        this.__searchResult = searchItems(value, this.__searchItems);
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.handlers.keydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.handlers.keydown);
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          font-family: ${CSS_FONT_FAMILY};
          width: 745px;
        }

        .content {
          margin-top: 0;
        }

        .relative {
          position: relative;
        }

        .spacing-button {
          margin-right: 10px;
        }

        neb-button:focus {
          opacity: 0.8;
        }

        .container-buttons {
          margin-top: ${CSS_SPACING};
          display: flex;
        }

        .type-container {
          display: flex;
          gap: ${CSS_SPACING};
          align-items: start;
        }

        .paid-amount {
          padding-top: 26px;
        }

        .neb-width {
          width: 155px;
        }

        neb-select-search {
          padding-top: 16px;
          width: 100%;
        }

        .error-area {
          display: flex;
          flex-direction: column;
        }

        .paid-amount-error {
          color: ${CSS_COLOR_ERROR};
          padding-top: ${CSS_SPACING};
        }

        .paid-removal-error {
          padding-top: ${CSS_SPACING};
        }

        .plan-dropdown {
          width: 405px;
          margin: 15px 0;
        }

        .label-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-gap: ${CSS_SPACING};
          padding-top: 15px;
          grid-template-rows: auto;
        }

        .label-container-paid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 50px;

          grid-gap: ${CSS_SPACING};
          padding-top: 15px;
          grid-template-rows: auto;
        }

        .field-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-gap: ${CSS_SPACING};
          grid-template-rows: auto;
        }

        .field-container-paid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 50px;
          grid-gap: ${CSS_SPACING};
          grid-template-rows: auto;
        }

        .field-container-remit-offset {
          display: grid;
          grid-gap: ${CSS_FIELD_MARGIN};
          column-gap: ${CSS_FIELD_MARGIN};
          grid-template-columns: 140px 140px auto;
        }

        .no-gap {
          grid-gap: 0;
        }

        .icon {
          fill: ${CSS_COLOR_HIGHLIGHT};
          cursor: pointer;
        }

        .icon-remove {
          width: ${CSS_SPACING};
          height: ${CSS_SPACING};
          margin: 25px 15px 0 0;
        }

        .payment-balance {
          display: flex;
          width: 100%;
          margin: ${CSS_SPACING_ROW} 0;
        }

        .text-error {
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: normal;
          width: 100%;
          min-width: 0px;
          font-size: ${CSS_FONT_SIZE_CAPTION};
          letter-spacing: 0.35px;
          color: ${CSS_COLOR_ERROR};
        }

        .spacer {
          width: ${CSS_SPACING};
          height: ${CSS_SPACING};
          margin: 25px 35px 0 0;
        }

        .insurance-select {
          padding-top: 16px;
          width: 100%;
        }
      `,
    ];
  }

  updated(changedProps) {
    if (changedProps.has('model')) {
      this.__initializePopupState();

      const formattedDropDownItems = this.model.paymentCodes
        .filter(isActivePatientPaymentCode)
        .map(({ data }) => ({
          label: `${data.code} - ${data.description}`,
          data: {
            id: data.id,
            code: data.code,
            description: data.description,
            label: data.code,
          },
        }));

      this.__patientInsuranceDropdownItems = [
        BLANK_TYPE_DROPDOWN_ITEM,
        ...this.model.patientInsurances,
      ];

      this.__searchItems = [
        BLANK_TYPE_DROPDOWN_ITEM,
        ...formattedDropDownItems,
      ];

      this.__searchResult = [
        BLANK_TYPE_DROPDOWN_ITEM,
        ...formattedDropDownItems,
      ];

      this.__handleInitialFocus();
    }
  }

  __handleInitialFocus() {
    setTimeout(() => {
      const hasNoPatientInsurances =
        this.__getFilteredPatientInsuranceDropdownItems().length <= 1;

      if (hasNoPatientInsurances) {
        const firstTextfield =
          this.shadowRoot.getElementById('amount-textfield-0');

        if (firstTextfield) {
          firstTextfield.setFocus();
        }
      } else {
        const searchSelects =
          this.shadowRoot.querySelectorAll('neb-select-search');
        const lastIndex = searchSelects.length - 1;
        const insuranceSearch = this.shadowRoot.getElementById(
          `insurance-select-${lastIndex}`,
        );

        if (insuranceSearch) {
          insuranceSearch.setFocus();
        }
      }
    }, 100);
  }

  __resolvePopupResult() {
    if (!this.__typeState[this.__typeState.length - 1].amount) {
      this.__typeState.splice(-1);
    }

    return {
      itemsToEdit: this.__typeState.filter(item => item.individualRowId),
      itemsToDelete: this.__itemsToDelete,
      itemsToAdd: this.__typeState.filter(item => !item.individualRowId),
      popupChanged: !equal(this.__typeState, this.initialTypeState),
    };
  }

  __initializePopupState() {
    this.title = this.model.title;
    this.__typeState = this.model.initialDebits.map(code => ({
      individualRowId: code.id || '',
      codeId: code.codePaymentId || '',
      amount: code.amount || 0,
      allocations: code.allocations || [],
      patientInsuranceId: code.patientInsuranceId || '',
      payerId: code.payerId || '',
    }));

    this.initialTypeState = this.__typeState.map(item =>
      deepCopy({
        individualRowId: item.individualRowId,
        codeId: item.codeId,
        amount: item.amount,
        patientInsuranceId: item.patientInsuranceId,
        allocations: item.allocations,
        payerId: item.payerId,
      }),
    );

    this.__errors = Array(this.__typeState.length).fill({
      codeId: false,
      amount: false,
      patientInsuranceId: false,
    });

    this.__addNewRow();

    this.__typeState.forEach((_, index) => {
      this.__validate(index);
    });
  }

  __addNewRow() {
    this.__typeState = [
      ...this.__typeState,
      {
        individualRowId: '',
        codeId: '',
        patientInsuranceId: '',
        payerId: '',
        amount: 0,
        allocations: [],
      },
    ];

    this.__errors = [
      ...this.__errors,
      {
        codeId: false,
        amount: false,
        patientInsuranceId: false,
        paidAmount: false,
      },
    ];
  }

  __removeRow(rowIndex) {
    if (this.__typeState[rowIndex].individualRowId) {
      this.__itemsToDelete.push(this.__typeState[rowIndex]);
    }

    const rowPaidAmount = this.__getPaidAmount(this.__typeState[rowIndex]);

    if (rowPaidAmount > 0) {
      this.paidAmountIsRemoved = true;
    }

    this.__typeState.splice(rowIndex, 1);
    this.__errors.splice(rowIndex, 1);
  }

  __isRowEmpty(rowIndex) {
    const { codeId, amount, patientInsuranceId } = this.__typeState[rowIndex];
    const lastRowIndex = this.__typeState.length - 1;

    return (
      (codeId === '' || codeId === null) &&
      amount === 0 &&
      patientInsuranceId === '' &&
      rowIndex !== lastRowIndex
    );
  }

  __hasErrors() {
    return this.__errors.some(
      e =>
        e.codeId === true ||
        e.amount === true ||
        e.paidAmount === true ||
        e.patientInsuranceId === true,
    );
  }

  __validate(rowIndex) {
    if (this.__typeState[rowIndex]) {
      const lastRowIndex = this.__typeState.length - 1;
      const { codeId, patientInsuranceId } = this.__typeState[rowIndex];
      const errorsCopy = [...this.__errors];

      const invalidCode = codeId === '' || codeId === null;
      const isLastRow = rowIndex === lastRowIndex;
      const isPrimaryInsurance =
        patientInsuranceId === this.model.primaryInsuranceId;

      errorsCopy[rowIndex] = {
        ...errorsCopy[rowIndex],
        codeId: invalidCode && !isLastRow && !isPrimaryInsurance,

        patientInsuranceId: patientInsuranceId === '' && !isLastRow,
      };

      this.__errors = errorsCopy;
    }
  }

  __getBillingCodesById(codeId) {
    if (!codeId) {
      return '';
    }

    return this.__searchItems.find(item => item.data.id === codeId) || '';
  }

  __getPaidAmount(initialCode) {
    return initialCode.allocations.reduce((sum, a) => sum + a.amount, 0);
  }

  __displayPaidAmount(initialCode, index) {
    const paidAmount = this.__getPaidAmount(initialCode);

    const paidAmountError = this.__errors[index].paidAmount;
    return html`
      <neb-text
        id="paid-amount-value-${index}"
        class="paid-amount"
        style="${paidAmountError ? `color:${CSS_COLOR_ERROR}` : ''}"
        >${centsToCurrency(paidAmount)}</neb-text
      >
    `;
  }

  __showPaidRemovalError() {
    return this.paidAmountIsRemoved
      ? html`
          <span class="paid-removal-error"
            >(WARNING) Selecting update will remove one or more
            allocations</span
          >
        `
      : '';
  }

  __renderPaidAmountError() {
    return html`
      <div class="error-area" id="${ELEMENTS.displayedErrors.id}">
        ${this.__showPaidRemovalError()}
      </div>
    `;
  }

  __renderFieldHeader() {
    const fieldHeader = HEADER;

    return html`
      <div class="label-container-paid">
        ${fieldHeader.map(
          (header, index) => html`
            <span id="field-header-${index}">${header}</span>
          `,
        )}
      </div>
    `;
  }

  __getAmountTextfieldError(index) {
    return this.__errors[index].amount || this.__errors[index].paidAmount;
  }

  __getAmountTextfieldHelper(index) {
    const { amount } = this.__errors[index];

    if (amount) {
      return 'Cannot be equal to $0.00';
    }

    return '';
  }

  __getPatientInsurance(patientInsuranceId) {
    return this.__patientInsuranceDropdownItems.find(
      insurance => insurance.data.id === patientInsuranceId,
    );
  }

  __shouldDisableSearch(index) {
    return (
      this.__typeState[index].patientInsuranceId ===
      this.model.primaryInsuranceId
    );
  }

  __getFilteredPatientInsuranceDropdownItems() {
    const doesPrimaryInsuranceRowExist = this.__typeState.some(
      item => item.patientInsuranceId === this.model.primaryInsuranceId,
    );

    return !doesPrimaryInsuranceRowExist
      ? this.__patientInsuranceDropdownItems
      : this.__patientInsuranceDropdownItems.filter(
          item => item.data.id !== this.model.primaryInsuranceId,
        );
  }

  __renderRows() {
    return html`
      <div id="${ELEMENTS.fieldContainer.id}" class="field-container-paid">
        ${this.__typeState.map((initialCode, index) => {
          const RowWithAllocations = initialCode.allocations.length > 0;

          return html`
            <neb-select
              id="insurance-select-${index}"
              name="insurance-select-${index}"
              class="insurance-select"
              .helper="${this.__errors[index].patientInsuranceId
                ? 'Must have a insurance selected'
                : ''}"
              .error="${this.__errors[index].patientInsuranceId}"
              .items="${this.__getFilteredPatientInsuranceDropdownItems()}"
              .value="${this.__getPatientInsurance(
                initialCode.patientInsuranceId,
              )}"
              .onChange="${this.handlers.changeInsurance}"
              itemMinWidth="300"
              showFullText
              wrapText
              itemHeight="60"
              ?disabled="${RowWithAllocations}"
            ></neb-select>
            <neb-select-search
              id="type-search-${index}"
              name="type-search-${index}"
              .helper="${this.__errors[index].codeId
                ? 'Must have a type selected'
                : ''}"
              .error="${this.__errors[index].codeId}"
              .items="${this.__searchResult}"
              .value="${this.__getBillingCodesById(initialCode.codeId)}"
              .onSearch="${this.handlers.searchCode}"
              .showMenu="${this.__showResults}"
              .onChange="${this.handlers.changeType}"
              ?disabled="${this.__shouldDisableSearch(index)}"
              itemMinWidth="600"
              showSearch
              showFullText
              wrapText
              itemHeight="60"
            ></neb-select-search>
            <neb-textfield
              id="amount-textfield-${index}"
              label=" "
              name="amount-textfield-${index}"
              .value="${centsToCurrency(initialCode.amount)}"
              .error="${this.__getAmountTextfieldError(index)}"
              .helper="${this.__getAmountTextfieldHelper(index)}"
              .mask="${masks.currency}"
              .inputMode="${'numeric'}"
              maxLength="11"
              .onChange="${this.handlers.changeAmount}"
            ></neb-textfield>
            ${this.__displayPaidAmount(initialCode, index)}
          `;
        })}
      </div>
    `;
  }

  __renderButtons() {
    return html`
      <div class="container-buttons">
        <neb-button
          id="${ELEMENTS.buttonSave.id}"
          class="spacing-button"
          label="Update"
          .onClick="${this.handlers.save}"
        ></neb-button>
        <neb-button
          id="${ELEMENTS.buttonCancel.id}"
          role="${BUTTON_ROLE.CANCEL}"
          label="Cancel"
          .onClick="${this.handlers.cancel}"
        ></neb-button>
      </div>
    `;
  }

  renderContent() {
    return html`
      ${this.__renderFieldHeader()} ${this.__renderRows()}
      ${this.__renderPaidAmountError()} ${this.__renderButtons()}
    `;
  }

  render() {
    return html`
      <div class="container relative">
        ${this.renderHeader()}
        <div class="content">${this.renderContent()}</div>
      </div>
    `;
  }
}

customElements.define(
  'neb-popup-other-responsibility',
  NebPopupOtherResponsibility,
);
